/**
 * GREAT WORKS - Map-bound wonders requiring tile clusters
 *
 * Great Works are special wonders that require placing on the world map.
 * They use proximity-based requirements, not strict geometric patterns.
 *
 * Requirement types:
 * - anchor: Biome(s) or category the center tile must be
 * - nearby: Tiles within range that must match criteria
 *
 * Early tiers are very forgiving (category + proximity).
 * Later tiers get stricter (specific biomes + connected).
 */

export const GREAT_WORKS = {
  // ===========================================
  // TIER 1 - PRIMITIVE AGE
  // Super easy: Any nature tile with 1 nature neighbor
  // ===========================================
  woodcutters_lodge: {
    id: 'woodcutters_lodge',
    name: "Woodcutter's Lodge",
    icon: '🪓',
    tier: 1,
    description: 'A central hub for lumber operations in the wilderness.',
    flavorText: '"Where axes sing and timber falls."',
    buildTime: 300, // 5 minutes
    cost: {
      resources: { wood: 150, stone: 50 },
    },
    mapRequirement: {
      anchor: { category: 'nature' }, // Forest, plains, grassland, etc.
      nearby: {
        category: 'nature',
        minCount: 1,
        range: 1, // Adjacent only
        connected: false,
      },
    },
    effects: {
      productionBonus: { wood: 0.20 },
      flatBonus: { wood: 0.5 },
    },
    lore: 'The sound of falling trees echoes through the forest as your woodcutters establish their base of operations.',
  },

  stone_circle: {
    id: 'stone_circle',
    name: 'Sacred Stone Circle',
    icon: '🪨',
    tier: 1,
    description: 'Ancient stones arranged to channel mystical energies.',
    flavorText: '"The stones remember what we have forgotten."',
    buildTime: 300,
    cost: {
      resources: { stone: 150, wood: 50 },
    },
    mapRequirement: {
      anchor: { category: 'stone' }, // Mountains, rocky outcrop, etc.
      nearby: {
        category: 'stone',
        minCount: 1,
        range: 2, // Within 2 hexes
        connected: false,
      },
    },
    effects: {
      productionBonus: { stone: 0.20, knowledge: 0.10 },
    },
    lore: 'Arranged in patterns known only to the ancients, these stones pulse with an inner light at dawn.',
  },

  frontier_camp: {
    id: 'frontier_camp',
    name: 'Frontier Camp',
    icon: '⛺',
    tier: 1,
    description: 'A well-supplied camp at the edge of explored territory.',
    flavorText: '"From here, we push into the unknown."',
    buildTime: 240,
    cost: {
      resources: { wood: 100, food: 100 },
    },
    mapRequirement: {
      // Super easy: ANY tile with 2+ different biome types nearby
      anchor: { category: ['nature', 'stone'] },
      nearby: {
        anyBiome: true, // Any biome counts
        minCount: 2,
        range: 2,
        connected: false,
        requireDiversity: 2, // At least 2 different biome types
      },
    },
    effects: {
      productionBonus: { food: 0.15 },
      flatBonus: { food: 0.3, population: 0.1 },
    },
    lore: 'Explorers and settlers gather here before venturing into uncharted lands.',
  },

  // ===========================================
  // TIER 2 - STONE AGE
  // Still easy: Category + 2 nearby + range 2
  // ===========================================
  hunters_guild: {
    id: 'hunters_guild',
    name: "Hunter's Guild",
    icon: '🏹',
    tier: 2,
    description: 'Master hunters share their skills and coordinate hunts.',
    flavorText: '"The wild provides for those who know its ways."',
    buildTime: 420,
    cost: {
      resources: { wood: 200, food: 150, stone: 100 },
    },
    mapRequirement: {
      anchor: { biomes: ['forest', 'plains', 'savanna', 'grassland'] },
      nearby: {
        category: 'nature',
        minCount: 2,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { food: 0.30 },
      flatBonus: { food: 1 },
    },
    lore: 'The guild hall displays trophies from legendary hunts. New members train under watchful masters.',
  },

  mining_collective: {
    id: 'mining_collective',
    name: 'Mining Collective',
    icon: '⛏️',
    tier: 2,
    description: 'A cooperative of miners pooling resources and knowledge.',
    flavorText: '"Deeper we dig, richer we grow."',
    buildTime: 420,
    cost: {
      resources: { stone: 250, wood: 150, iron: 50 },
    },
    mapRequirement: {
      anchor: { category: 'stone' },
      nearby: {
        category: 'stone',
        minCount: 2,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { stone: 0.30, iron: 0.20 },
    },
    lore: 'Pickaxes ring day and night as the collective unearths the mountain\'s treasures.',
  },

  riverside_market: {
    id: 'riverside_market',
    name: 'Riverside Market',
    icon: '🏪',
    tier: 2,
    description: 'A bustling market where river and land routes meet.',
    flavorText: '"All roads lead to the market."',
    buildTime: 480,
    cost: {
      resources: { wood: 200, gold: 100, food: 100 },
    },
    mapRequirement: {
      anchor: { biomes: ['coastal', 'riverDelta', 'oasis'] },
      nearby: {
        anyBiome: true,
        minCount: 3,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { gold: 0.35 },
      flatBonus: { gold: 0.5 },
    },
    lore: 'Merchants from distant lands gather here to trade exotic wares.',
  },

  // ===========================================
  // TIER 3 - AGRICULTURAL AGE
  // Medium: Specific biome anchor + 3 nearby from category
  // ===========================================
  forest_sanctuary: {
    id: 'forest_sanctuary',
    name: 'Forest Sanctuary',
    icon: '🌲',
    tier: 3,
    description: 'A protected grove where nature flourishes undisturbed.',
    flavorText: '"In the heart of the forest, life eternal."',
    buildTime: 600,
    cost: {
      resources: { wood: 400, stone: 200, food: 200 },
    },
    mapRequirement: {
      anchor: { biomes: ['forest', 'mysticGrove', 'livingForest'] },
      nearby: {
        category: 'nature',
        minCount: 3,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { wood: 0.45, food: 0.30 },
      flatBonus: { wood: 1.5 },
    },
    lore: 'Ancient trees tower overhead, their roots intertwined with the essence of the land itself.',
  },

  mountain_fortress: {
    id: 'mountain_fortress',
    name: 'Mountain Fortress',
    icon: '🏔️',
    tier: 3,
    description: 'An impregnable stronghold carved into living rock.',
    flavorText: '"Let them come. The mountain protects its own."',
    buildTime: 720,
    cost: {
      resources: { stone: 600, iron: 200, wood: 200 },
    },
    mapRequirement: {
      anchor: { biomes: ['mountains', 'windsweptPeaks'] },
      nearby: {
        category: 'stone',
        minCount: 3,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { stone: 0.50, iron: 0.35 },
      globalProductionBonus: 0.05,
    },
    lore: 'Cut from the mountain itself, this fortress has never fallen to siege.',
  },

  scholars_retreat: {
    id: 'scholars_retreat',
    name: "Scholar's Retreat",
    icon: '📚',
    tier: 3,
    description: 'A peaceful haven where the wise gather to study.',
    flavorText: '"Knowledge flows like water to those who listen."',
    buildTime: 600,
    cost: {
      resources: { stone: 300, wood: 200, knowledge: 150, gold: 200 },
    },
    mapRequirement: {
      anchor: { category: 'knowledge' },
      nearby: {
        category: ['knowledge', 'nature'],
        minCount: 2,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { knowledge: 0.50 },
      researchSpeedBonus: 0.15,
    },
    lore: 'Scrolls and tomes from every corner of the world fill the endless shelves.',
  },

  // ===========================================
  // TIER 4 - METALLURGY AGE
  // Harder: Need 4 nearby tiles, stricter biomes
  // ===========================================
  grand_forge: {
    id: 'grand_forge',
    name: 'Grand Forge',
    icon: '🔥',
    tier: 4,
    description: 'A legendary forge where master smiths create wonders.',
    flavorText: '"The fires burn eternal, fed by the earth itself."',
    buildTime: 900,
    cost: {
      resources: { iron: 500, coal: 300, stone: 400 },
    },
    mapRequirement: {
      anchor: { biomes: ['volcanic', 'mountains', 'sulfurSprings'] },
      nearby: {
        category: 'stone',
        minCount: 4,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { iron: 0.60, steel: 0.40, coal: 0.30 },
      flatBonus: { iron: 2 },
    },
    lore: 'The heat of a hundred furnaces keeps the forge alive, producing steel of legendary quality.',
  },

  trade_consortium: {
    id: 'trade_consortium',
    name: 'Trade Consortium',
    icon: '⚖️',
    tier: 4,
    description: 'A powerful alliance of merchants controlling major routes.',
    flavorText: '"Gold speaks louder than swords."',
    buildTime: 900,
    cost: {
      resources: { gold: 800, wood: 300, stone: 300 },
    },
    mapRequirement: {
      anchor: { category: 'water' },
      nearby: {
        anyBiome: true,
        minCount: 4,
        range: 3,
        connected: false,
        requireDiversity: 3, // Need 3 different biome types
      },
    },
    effects: {
      productionBonus: { gold: 0.70 },
      globalProductionBonus: 0.08,
    },
    lore: 'From this hub, caravans and ships depart to the farthest reaches of the known world.',
  },

  nature_shrine: {
    id: 'nature_shrine',
    name: 'Shrine of the Wild',
    icon: '🦌',
    tier: 4,
    description: 'A sacred place where nature spirits are honored.',
    flavorText: '"The forest has eyes, and they watch over us."',
    buildTime: 780,
    cost: {
      resources: { wood: 600, food: 400, knowledge: 200, gold: 200 },
    },
    mapRequirement: {
      anchor: { category: 'nature' },
      nearby: {
        category: 'nature',
        minCount: 5,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { food: 0.55, wood: 0.45 },
      flatBonus: { food: 2, wood: 1 },
    },
    lore: 'Offerings are made beneath ancient oaks, and the spirits answer with bountiful harvests.',
  },

  // ===========================================
  // TIER 5 - KNOWLEDGE AGE
  // Now requiring connected tiles
  // ===========================================
  great_observatory: {
    id: 'great_observatory',
    name: 'Great Observatory',
    icon: '🔭',
    tier: 5,
    description: 'A towering structure to study the heavens.',
    flavorText: '"The stars hold all answers."',
    buildTime: 1080,
    cost: {
      resources: { stone: 800, gold: 500, knowledge: 400, gems: 50 },
    },
    mapRequirement: {
      anchor: { biomes: ['mountains', 'windsweptPeaks', 'celestialObservatory'] },
      nearby: {
        category: ['stone', 'knowledge'],
        minCount: 4,
        range: 2,
        connected: true, // Now requires connection
      },
    },
    effects: {
      productionBonus: { knowledge: 0.70, science: 0.50 },
      researchSpeedBonus: 0.25,
    },
    lore: 'The great lens reveals worlds beyond worlds, stars beyond counting.',
  },

  arcane_nexus: {
    id: 'arcane_nexus',
    name: 'Arcane Nexus',
    icon: '🔮',
    tier: 5,
    description: 'Where ley lines converge and magic flows freely.',
    flavorText: '"Reality bends at this crossroads of power."',
    buildTime: 1200,
    cost: {
      resources: { magic: 300, crystals: 100, stone: 500, knowledge: 300 },
    },
    mapRequirement: {
      anchor: { category: 'magical' },
      nearby: {
        category: ['magical', 'knowledge'],
        minCount: 3,
        range: 2,
        connected: false,
      },
    },
    effects: {
      productionBonus: { magic: 0.80, crystals: 0.50 },
      flatBonus: { magic: 2 },
    },
    lore: 'Mages from across the realm pilgrimage here to tap into the wellspring of arcane power.',
  },

  // ===========================================
  // TIER 6+ - MORE CHALLENGING
  // These require larger connected clusters
  // ===========================================
  world_wonder_lumber: {
    id: 'world_wonder_lumber',
    name: 'The Great Lumber Consortium',
    icon: '🌳',
    tier: 6,
    description: 'An empire of forestry spanning the known world.',
    flavorText: '"From sapling to ship, we shape the world."',
    buildTime: 1500,
    cost: {
      resources: { wood: 3000, gold: 1000, iron: 500 },
    },
    mapRequirement: {
      anchor: { biomes: ['forest', 'livingForest'] },
      nearby: {
        category: 'nature',
        minCount: 6,
        range: 3,
        connected: true,
      },
    },
    effects: {
      productionBonus: { wood: 1.00 },
      globalProductionBonus: 0.12,
      flatBonus: { wood: 5 },
    },
    lore: 'A thousand sawmills work in harmony, transforming endless timber into the backbone of civilization.',
  },

  elemental_convergence: {
    id: 'elemental_convergence',
    name: 'Elemental Convergence',
    icon: '💫',
    tier: 7,
    description: 'A nexus where all elemental forces meet.',
    flavorText: '"Fire, water, earth, and air dance as one."',
    buildTime: 1800,
    cost: {
      resources: { magic: 2000, crystals: 500, energy: 300, gems: 200 },
    },
    mapRequirement: {
      anchor: { category: 'magical' },
      nearby: {
        category: ['magical', 'stone', 'water'],
        minCount: 5,
        range: 3,
        connected: true,
        requireDiversity: 3,
      },
    },
    effects: {
      productionBonus: { magic: 1.20, energy: 0.80 },
      globalProductionBonus: 0.18,
    },
    lore: 'At this convergence point, the raw forces of creation can be harnessed and shaped.',
  },
};

// ===========================================
// BALANCE CONSTANTS
// ===========================================
export const GREAT_WORKS_BALANCE = {
  maxTotal: 5, // Can have up to 5 Great Works
  maxUnderConstruction: 1,
  canCancel: true,
  cancelRefundPercent: 0.5,

  tierNames: {
    1: 'Primitive',
    2: 'Stone Age',
    3: 'Agricultural',
    4: 'Metallurgy',
    5: 'Knowledge',
    6: 'Industrial',
    7: 'Advanced',
  },
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

export function getGreatWorksByTier(tier) {
  return Object.values(GREAT_WORKS).filter(w => w.tier === tier);
}

export function getGreatWork(workId) {
  return GREAT_WORKS[workId] || null;
}

export function getGreatWorkTiers() {
  const tiers = new Set(Object.values(GREAT_WORKS).map(w => w.tier));
  return Array.from(tiers).sort((a, b) => a - b);
}

export default GREAT_WORKS;
